import { type SelectionRange } from '../../../state/types.ts';
import { type SelectionAxis } from '../../types.ts';

import { getRangeMinMax } from './get-range-min-max.ts';

type ExtendRangeParams = {
  range: SelectionRange;
  newIndex: number;
  axis: SelectionAxis;
};

/**
 * Extends a selection range to include a new index for the given axis.
 * Handles extension at both the start and end of the range.
 * Preserves secondary axis coordinates from the input range.
 * @param range - The range to extend
 * @param newIndex - The new index to include on the given axis
 * @param axis - The axis to extend ('row' or 'col')
 * @returns A new extended range with the same ID
 */
export function extendRange({
  range,
  newIndex,
  axis
}: ExtendRangeParams): SelectionRange {
  const { min, max } = getRangeMinMax(range, axis);
  const newMin = Math.min(min, newIndex);
  const newMax = Math.max(max, newIndex);

  const anchorIndex = range.anchor[axis].index;
  const extendingForward = newIndex > max;
  const extendingBackward = newIndex < min;

  let newAnchor = range.anchor;
  let newLead = range.lead;

  if (extendingForward) {
    if (anchorIndex === max) {
      newAnchor = { ...range.anchor, [axis]: { index: newMax } };
    } else {
      newLead = { ...range.lead, [axis]: { index: newMax } };
    }
  } else if (extendingBackward) {
    if (anchorIndex === min) {
      newAnchor = { ...range.anchor, [axis]: { index: newMin } };
    } else {
      newLead = { ...range.lead, [axis]: { index: newMin } };
    }
  }

  return {
    id: range.id,
    anchor: newAnchor,
    lead: newLead
  };
}
